/** 
 * Product Mobile Image Gallery
 * Simple mobile implementation matching desktop functionality
 * - Featured image on top
 * - Thumbnails below in horizontal scroll
 * - Click thumbnails to change featured image
 * - Prev/Next buttons on featured image
 * - Lightbox support
 */
(function () {
  'use strict';

  let currentIndex = 0;
  let mediaItems = [];
  let isInitialized = false;
  let observer = null;
  let featuredImageTransitionTimeout = null;

  /**
   * Check if device is mobile/tablet
   */
  function checkMobile() {
    return window.innerWidth <= 1024;
  }

  /**
   * Initialize mobile gallery
   */
  function initMobileGallery() {
    if (isInitialized) return;

    const productMediaList = document.querySelector('.brator-product-img-tab-list');
    if (!productMediaList) return;

    if (!checkMobile()) return;

    // Get all media items
    mediaItems = Array.from(productMediaList.querySelectorAll('.js-tabs__content'));
    if (mediaItems.length === 0) return;

    // Hide original structure on mobile but keep it accessible for lightbox
    // Use visibility instead of display so PhotoSwipe can still find the elements
    productMediaList.style.visibility = 'hidden';
    productMediaList.style.position = 'absolute';
    productMediaList.style.width = '1px';
    productMediaList.style.height = '1px';
    productMediaList.style.overflow = 'hidden';
    productMediaList.style.opacity = '0';
    productMediaList.style.pointerEvents = 'none';

    // Create mobile container
    const mobileContainer = document.createElement('div');
    mobileContainer.className = 'product-mobile-gallery';

    // Create featured image container
    const featuredContainer = document.createElement('div');
    featuredContainer.className = 'product-mobile-featured-container';

    // Create prev/next buttons
    const prevBtn = document.createElement('button');
    prevBtn.className = 'product-mobile-nav-btn product-mobile-nav-prev';
    prevBtn.setAttribute('aria-label', 'Previous image');
    prevBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
        </svg>
      `;

    const nextBtn = document.createElement('button');
    nextBtn.className = 'product-mobile-nav-btn product-mobile-nav-next';
    nextBtn.setAttribute('aria-label', 'Next image');
    nextBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
        </svg>
      `;

    // Create featured image wrapper
    const featuredWrapper = document.createElement('div');
    featuredWrapper.className = 'product-mobile-featured-wrapper';

    // Clone first image as featured
    const firstItem = mediaItems[0].cloneNode(true);
    firstItem.classList.remove('js-tabs__content');
    firstItem.style.display = 'block';
    firstItem.style.visibility = 'visible';
    firstItem.style.opacity = '1';
    firstItem.style.transition = 'opacity 0.3s ease';
    firstItem.classList.add('product-mobile-featured-item', 'is-active');
    firstItem.setAttribute('data-index', 0);
    featuredWrapper.appendChild(firstItem);

    featuredContainer.appendChild(prevBtn);
    featuredContainer.appendChild(nextBtn);
    featuredContainer.appendChild(featuredWrapper);

    // Add swipe support on featured area (mobile only)
    let touchStartX = 0;
    let touchEndX = 0;
    const swipeThreshold = 50; // pixels

    featuredWrapper.addEventListener('touchstart', function (e) {
      if (!e.touches || e.touches.length === 0) return;
      touchStartX = e.touches[0].clientX;
      touchEndX = touchStartX;
    }, { passive: true });

    featuredWrapper.addEventListener('touchmove', function (e) {
      if (!e.touches || e.touches.length === 0) return;
      touchEndX = e.touches[0].clientX;
    }, { passive: true });

    featuredWrapper.addEventListener('touchend', function () {
      const deltaX = touchEndX - touchStartX;
      if (Math.abs(deltaX) > swipeThreshold) {
        if (deltaX < 0) {
          // swipe left -> next
          const newIndex = currentIndex < mediaItems.length - 1 ? currentIndex + 1 : 0;
          setFeaturedImage(newIndex);
        } else {
          // swipe right -> prev
          const newIndex = currentIndex > 0 ? currentIndex - 1 : mediaItems.length - 1;
          setFeaturedImage(newIndex);
        }
      }
      touchStartX = 0;
      touchEndX = 0;
    }, { passive: true });

    // Create thumbnails container
    const thumbnailsContainer = document.createElement('div');
    thumbnailsContainer.className = 'product-mobile-thumbnails-container';

    // Create thumbnails scroll wrapper
    const thumbnailsScroll = document.createElement('div');
    thumbnailsScroll.className = 'product-mobile-thumbnails-scroll';

    // Create thumbnails
    mediaItems.forEach((item, index) => {
      const thumbnail = document.createElement('button');
      thumbnail.className = 'product-mobile-thumbnail';
      thumbnail.setAttribute('data-index', index);
      thumbnail.setAttribute('aria-label', `View image ${index + 1}`);

      if (index === 0) {
        thumbnail.classList.add('is-active');
      }

      // Get image from the item
      const img = item.querySelector('img');
      if (img) {
        const thumbnailImg = img.cloneNode(true);
        thumbnailImg.style.width = '100%';
        thumbnailImg.style.height = '100%';
        thumbnailImg.style.objectFit = 'cover';
        thumbnail.appendChild(thumbnailImg);
      }

      thumbnail.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        setFeaturedImage(index);
      });

      thumbnailsScroll.appendChild(thumbnail);
    });

    thumbnailsContainer.appendChild(thumbnailsScroll);
    mobileContainer.appendChild(featuredContainer);
    mobileContainer.appendChild(thumbnailsContainer);

    // Insert before original list
    productMediaList.parentNode.insertBefore(mobileContainer, productMediaList);

    // Trigger lightbox re-initialization after mobile gallery is created
    setTimeout(function () {
      const event = new CustomEvent('productLightboxUpdate');
      document.dispatchEvent(event);
    }, 100);

    // Add event listeners
    prevBtn.addEventListener('click', function () {
      const newIndex = currentIndex > 0 ? currentIndex - 1 : mediaItems.length - 1;
      setFeaturedImage(newIndex);
    });

    nextBtn.addEventListener('click', function () {
      const newIndex = currentIndex < mediaItems.length - 1 ? currentIndex + 1 : 0;
      setFeaturedImage(newIndex);
    });

    // Update button states
    updateNavButtons();

    // Sync with desktop gallery changes (variants)
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            const target = mutation.target;
            // global.js sets display usually to 'unset' or block, and others to 'none'
            // We check if it is NOT none, implying it became visible
            if (target.style.display && target.style.display !== 'none') {
              const index = mediaItems.indexOf(target);
              if (index !== -1 && index !== currentIndex) {
                setFeaturedImage(index);
              }
            }
          }
        });
      });

      mediaItems.forEach((item) => {
        observer.observe(item, { attributes: true, attributeFilter: ['style'] });
      });
    }

    isInitialized = true;
  }

  /**
   * Set featured image by index
   */
  function setFeaturedImage(index) {
    if (index < 0 || index >= mediaItems.length) return;

    currentIndex = index;

    // Cancel any pending transition to prevent race condition on rapid clicks
    if (featuredImageTransitionTimeout) {
      clearTimeout(featuredImageTransitionTimeout);
      featuredImageTransitionTimeout = null;
    }

    const featuredWrapper = document.querySelector('.product-mobile-featured-wrapper');
    if (!featuredWrapper) return;

    // Remove active class from current
    const currentFeatured = featuredWrapper.querySelector('.product-mobile-featured-item');
    if (currentFeatured) {
      currentFeatured.classList.remove('is-active');
      currentFeatured.style.opacity = '0';
    }

    // Clone and add new featured image (single timeout - cancels previous on rapid click)
    featuredImageTransitionTimeout = setTimeout(function () {
      const newItem = mediaItems[index].cloneNode(true);
      newItem.classList.remove('js-tabs__content');
      newItem.style.display = 'block';
      newItem.style.visibility = 'visible';
      newItem.style.opacity = '0';
      newItem.style.transition = 'opacity 0.3s ease';
      newItem.classList.add('product-mobile-featured-item', 'is-active');
      newItem.setAttribute('data-index', index);

      if (currentFeatured) {
        currentFeatured.remove();
      }

      featuredWrapper.appendChild(newItem);

      // Fade in
      setTimeout(function () {
        newItem.style.opacity = '1';
        newItem.style.visibility = 'visible';
      }, 10);

      featuredImageTransitionTimeout = null;
    }, 150);

    // Update thumbnails
    const thumbnails = document.querySelectorAll('.product-mobile-thumbnail');
    thumbnails.forEach((thumb, idx) => {
      if (idx === index) {
        thumb.classList.add('is-active');
      } else {
        thumb.classList.remove('is-active');
      }
    });

    // Update nav buttons
    updateNavButtons();

    // Re-initialize lightbox for new image
    setTimeout(function () {
      if (typeof PhotoSwipe !== 'undefined' && typeof PhotoSwipeLightbox !== 'undefined') {
        // Find all lightbox links including mobile gallery
        const lightboxLinks = document.querySelectorAll('.product-mobile-gallery .product__media-lightbox, .brator-product-img-tab-list .product__media-lightbox');
        if (lightboxLinks.length > 0) {
          // The existing lightbox should pick up the new links automatically
          // If not, we can trigger a custom event or re-initialize
          const event = new CustomEvent('productLightboxUpdate');
          document.dispatchEvent(event);
        }
      }
    }, 200);
  }

  /**
   * Update navigation button states
   */
  function updateNavButtons() {
    const prevBtn = document.querySelector('.product-mobile-nav-prev');
    const nextBtn = document.querySelector('.product-mobile-nav-next');

    if (prevBtn && nextBtn) {
      // Always enabled for looping
      prevBtn.disabled = false;
      nextBtn.disabled = false;
    }
  }

  /**
   * Destroy mobile gallery
   */
  function destroyMobileGallery() {
    if (featuredImageTransitionTimeout) {
      clearTimeout(featuredImageTransitionTimeout);
      featuredImageTransitionTimeout = null;
    }

    const mobileContainer = document.querySelector('.product-mobile-gallery');
    if (mobileContainer) {
      mobileContainer.remove();
    }

    const productMediaList = document.querySelector('.brator-product-img-tab-list');
    if (productMediaList) {
      // Restore original structure visibility
      productMediaList.style.visibility = '';
      productMediaList.style.position = '';
      productMediaList.style.width = '';
      productMediaList.style.height = '';
      productMediaList.style.overflow = '';
      productMediaList.style.opacity = '';
      productMediaList.style.pointerEvents = '';
    }

    if (observer) {
      observer.disconnect();
      observer = null;
    }

    isInitialized = false;
    currentIndex = 0;
    mediaItems = [];
  }

  /**
   * Handle resize
   */
  function handleResize() {
    const isMobile = checkMobile();

    if (isMobile && !isInitialized) {
      initMobileGallery();
    } else if (!isMobile && isInitialized) {
      destroyMobileGallery();
    }
  }

  // Initialize on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(initMobileGallery, 100);
      window.addEventListener('resize', handleResize);
    });
  } else {
    setTimeout(initMobileGallery, 100);
    window.addEventListener('resize', handleResize);
  }
})();
